import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { handleInitGame } from "../../service";
import { jwtDecode } from "jwt-decode";
import { loadAssets } from "./assetsSlice";
import { loadSounds } from "./soundsSlice";
import { rgsPlay } from "./spinSlice";

// Async thunk for initializing the game and fetching initial data
export const initializeGame = createAsyncThunk(
  "gameSettings/initializeGame",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      await Promise.all([dispatch(loadAssets()), dispatch(loadSounds())]);
      const result = await handleInitGame(); // API call to fetch game data
      const { token } = result;
      const decodedToken = jwtDecode(token); // Decode JWT token to extract game details

      return { result, exp: decodedToken.exp - Math.floor(Date.now() / 1000) };
    } catch (error) {
      return rejectWithValue(error.message); // Pass error message to the rejected case
    }
  }
);

// Initial state for the game settings slice
const initialState = {
  token: "",
  balance: 0,
  bet: 0,
  bets: [],
  currency: "",
  exp: 0,
  jackpots: [],
  paytable: [],
  round: [{ reels: [], wins: [] }],
  currentRound: {
    reels: [],
    wins: [],
    win: 0,
    winType: "",
    gameType: "",
    freeSpins: 0,
  },
  winType: "",
  gameType: "",
  winningLines: [],
  status: "idle",
  currentRoundIndex: 0,
  error: null,
  sessionExpired: false,

  // Free Spin Logic
  totalFreeSpinWin: 0,
  currentFreeSpinWin: 0,
};

// Create a slice for managing game settings
const gameSettingsSlice = createSlice({
  name: "gameSettings",
  initialState,
  reducers: {
    incrementBet(state) {
      const currentIndex = state.bets.indexOf(state.bet);
      if (currentIndex < state.bets.length - 1) {
        state.bet = state.bets[currentIndex + 1];
      }
    },

    decrementBet(state) {
      const currentIndex = state.bets.indexOf(state.bet);
      if (currentIndex > 0) {
        state.bet = state.bets[currentIndex - 1];
      }
    },

    setBalance(state, action) {
      state.balance = action.payload;
    },

    checkSessionTime(state) {
      const currentTime = Math.floor(Date.now() / 1000); // Get current time in seconds
      if (state.exp > 0 && state.exp <= currentTime) {
        state.sessionExpired = true; // Mark the session as expired
      }
    },

    incrementRoundIndex(state) {
      state.currentRoundIndex += 1;

      // ✅ Always get the correct round without overriding with undefined
      const nextRound =
        state.round[state.currentRoundIndex] ?? state.currentRound;

      // ✅ Update all state values at once
      state.currentRound = nextRound;
      state.currentFreeSpinWin = nextRound?.win ?? 0; // Ensure it's always a number
      state.totalFreeSpinWin += state.currentFreeSpinWin; // Accumulate win total
    },

    resetRoundIndex(state) {
      state.currentRoundIndex = 0;
      state.currentRound = state.round[0] ?? {
        reels: [],
        wins: [],
        win: 0,
        winType: "",
        gameType: "",
        freeSpins: 0,
      };
    },

    setGameType(state, action) {
      state.gameType = action.payload;
    },

    // TESTING PURPOSES ONLY
    setWinType(state, action) {
      state.winType = action.payload; // Update win type
    },

    // TESTING PURPOSES ONLY
    resetFreeSpinWins(state) {
      state.totalFreeSpinWin = 0;
      state.currentFreeSpinWin = 0;
    },

    reloadGame() {
      window.location.reload();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initializeGame.pending, (state) => {
        state.status = "loading";
        state.error = null;
        state.isLoading = true;
      })
      .addCase(initializeGame.fulfilled, (state, action) => {
        const { result, exp } = action.payload;
        state.token = result.token;
        state.balance = result.balance;
        state.bet = result.bet;
        state.bets = result.bets;
        state.currency = result.currency;
        state.jackpots = result.jackpots || [];
        state.paytable = result.paytable || [];
        state.round = result.round || state.round;
        state.currentRound = result.round
          ? result.round[state.currentRoundIndex]
          : state.currentRound;
        state.exp = exp;
        state.status = "succeeded";
        state.isLoading = false;
        state.winningLines = result.winningLines || [];
      })
      .addCase(initializeGame.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to initialize the game";
        state.isLoading = false;
      })

      // Listen for informations from spinSlice
      .addCase(rgsPlay.pending, (state) => {
        state.winType = "";

        if (state.balance >= state.bet) {
          state.balance -= state.bet;
          state.error = null;
          state.status = "spinning";
        } else {
          state.error = "Insufficient balance";
        }
      })
      .addCase(rgsPlay.fulfilled, (state, action) => {
        const { token, balance, bet, jackpots, round, gameType } =
          action.payload;
        state.token = token || state.token;

        if (state.gameType !== "feature") {
          state.balance = balance !== undefined ? balance : state.balance;
        }

        state.bet = bet !== undefined ? bet : state.bet;
        state.jackpots = jackpots || state.jackpots;
        state.round = round || state.round;
        state.gameType = gameType || state.gameType;

        state.currentRound = round
          ? round[state.currentRoundIndex]
          : state.currentRound;

        if (state.gameType === "feature" && state.currentRound) {
          state.totalFreeSpinWin += state.currentRound.win;
          state.currentFreeSpinWin += state.currentRound.win;
        }
      })
      .addCase(rgsPlay.rejected, (state, action) => {
        state.error = action.payload || "Spin failed"; // Store error globally if needed
      });
  },
});

// Export actions for use in components or other slices
export const {
  incrementBet,
  decrementBet,
  checkSessionTime,
  reloadGame,
  setWinType,
  incrementRoundIndex,
  resetRoundIndex,
  setGameType,
  setBalance,
  resetFreeSpinWins,
} = gameSettingsSlice.actions;

// Export the reducer to integrate into the Redux store
export default gameSettingsSlice.reducer;
